import React, { useEffect, useState } from "react";
import api, { apiError, openPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, Loader } from "@/components/common";
import { TextField, SelectField, DateField, AreaField } from "@/components/fields";
import { money, fmtDate, BILLING_FREQUENCIES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Scroll, Plus, FilePdf, ArrowsClockwise, XCircle, Megaphone } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { property_id: "", tenant_id: "", start_date: "", end_date: "", monthly_rent: 0, deposit: 0, billing_frequency: "monthly", landlord_po_box: "", landlord_location: "", notes: "" };

export default function Leases() {
  const { user, company } = useAuth();
  const cur = company?.currency || "KES";
  const [items, setItems] = useState(null);
  const [props, setProps] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [action, setAction] = useState(null); // {lease, type}
  const [reason, setReason] = useState("");
  const [newEnd, setNewEnd] = useState("");

  const canManage = ["property_manager", "property_admin"].includes(user.role);

  const load = () => {
    api.get("/leases").then((r) => setItems(r.data));
    api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
    api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.property_id || !form.tenant_id || !form.start_date || !form.end_date)
      return toast.error("Property, tenant and dates are required");
    try {
      await api.post("/leases", form);
      toast.success("Lease created"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doAction = async () => {
    try {
      if (action.type === "notice") {
        await api.post(`/leases/${action.lease.id}/notice-to-vacate`, { action: "notice", reason });
        toast.success("Notice to vacate issued");
      } else {
        await api.post(`/leases/${action.lease.id}/action`, { action: action.type, reason, new_end_date: newEnd || null });
        toast.success(`Lease ${action.type}d`);
      }
      setAction(null); setReason(""); setNewEnd(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!items) return <Loader />;

  return (
    <div>
      <PageHeader title="Leases" subtitle="Full lease lifecycle management" icon={Scroll}
        actions={canManage && <Button onClick={() => { setForm(empty); setOpen(true); }} data-testid="add-lease-btn" className="gap-2"><Plus size={16} /> Create Lease</Button>} />

      {items.length === 0 ? (
        <EmptyState icon={Scroll} title="No leases yet" message="Create a lease agreement once a tenant is onboarded."
          action={canManage && <Button onClick={() => setOpen(true)}><Plus size={16} className="mr-2" />Create Lease</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Tenant</TableHead><TableHead>Property</TableHead><TableHead>Period</TableHead>
                <TableHead className="text-right">Rent</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((l) => (
                  <TableRow key={l.id} data-testid={`lease-row-${l.id}`}>
                    <TableCell className="font-medium">{l.tenant_name}</TableCell>
                    <TableCell className="text-sm">{l.property_name} {l.unit_no}</TableCell>
                    <TableCell className="text-sm">{fmtDate(l.start_date)} → {fmtDate(l.end_date)}</TableCell>
                    <TableCell className="text-right font-mono">{money(l.monthly_rent, cur)}</TableCell>
                    <TableCell><StatusBadge status={l.status} /></TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end flex-wrap">
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => openPdf(`/leases/${l.id}/document`)} data-testid={`lease-pdf-${l.id}`}><FilePdf size={15} /> Lease</Button>
                        {canManage && l.status === "active" && <>
                          <Button size="icon" variant="ghost" title="Renew" onClick={() => setAction({ lease: l, type: "renew" })}><ArrowsClockwise size={16} /></Button>
                          <Button size="icon" variant="ghost" title="Notice to Vacate" onClick={() => setAction({ lease: l, type: "notice" })}><Megaphone size={16} /></Button>
                          <Button size="icon" variant="ghost" title="Terminate" onClick={() => setAction({ lease: l, type: "terminate" })}><XCircle size={16} className="text-destructive" /></Button>
                        </>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Lease Agreement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <SelectField label="Property" value={form.property_id} onChange={(v) => set("property_id", v)}
                options={props.map((p) => ({ value: p.id, label: `${p.name} ${p.unit_no || ""}` }))} required testId="lease-property" />
              <SelectField label="Tenant" value={form.tenant_id} onChange={(v) => set("tenant_id", v)}
                options={tenants.map((t) => ({ value: t.id, label: t.full_name }))} required testId="lease-tenant" />
              <DateField label="Start Date" value={form.start_date} onChange={(v) => set("start_date", v)} required testId="lease-start" />
              <DateField label="End Date" value={form.end_date} onChange={(v) => set("end_date", v)} required testId="lease-end" />
              <TextField label="Monthly Rent" type="number" value={form.monthly_rent} onChange={(v) => set("monthly_rent", v)} testId="lease-rent" />
              <TextField label="Deposit" type="number" value={form.deposit} onChange={(v) => set("deposit", v)} testId="lease-deposit" />
              <SelectField label="Billing Frequency" value={form.billing_frequency} onChange={(v) => set("billing_frequency", v)} options={BILLING_FREQUENCIES} />
              <TextField label="Landlord P.O. Box" value={form.landlord_po_box} onChange={(v) => set("landlord_po_box", v)} />
              <TextField label="Landlord Location" value={form.landlord_location} onChange={(v) => set("landlord_location", v)} />
            </div>
            <AreaField label="Notes" value={form.notes} onChange={(v) => set("notes", v)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-lease-btn">Create Lease</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!action} onOpenChange={() => setAction(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="capitalize">
            {action?.type === "notice" ? "Notice to Vacate" : `${action?.type} Lease`}
          </DialogTitle></DialogHeader>
          <div className="space-y-4">
            {action?.type === "renew" && <DateField label="New End Date" value={newEnd} onChange={setNewEnd} />}
            <AreaField label="Reason / Note" value={reason} onChange={setReason} testId="action-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)}>Cancel</Button>
            <Button onClick={doAction} data-testid="confirm-action-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
